/**
 * Shared types used by both server and (a subset by) the client.
 *
 * Coordinate convention: ALL placement coordinates in the field map are in PDF
 * user space (bottom-left origin), the same space pdf-lib draws in. For text,
 * `y` is the text baseline. Helpers in `server/pdf/coords.ts` convert from the
 * browser/top-left origin when calibrating.
 */

import type { NativeIntakeProfile } from './nativeIntake.js';
export { NATIVE_INTAKE_PROFILE_VERSION, NATIVE_INTAKE_PROFILE_VERSIONS, NATIVE_INTAKE_FIELDS, nativeIntakeFields, nativeScannerManufacturer, isNativeIntakeProfileVersion, readNativeIntakeProfile } from './nativeIntake.js';
export type { NativeIntakeProfile, NativeIntakeProfileVersion, NativeIntakeField, NativeIntakeFieldKey } from './nativeIntake.js';

export type Alignment = "left" | "center" | "right";

export interface TextPlacement {
  page: number;
  x: number;
  y: number;
  width?: number;
  height?: number;
  fontSize: number;
  alignment?: Alignment;
  multiline?: boolean;
}

export interface CheckboxPlacement {
  page: number;
  x: number;
  y: number;
  size?: number;
}

export interface SignaturePlacement {
  page: number;
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface PdfFieldMap {
  version: string;
  sourcePdfSha256: string;
  /** logicalField -> existing AcroForm field name (hybrid fill). Empty here: packet has no AcroForm fields. */
  acroFields?: Record<string, string>;
  /** logicalField -> stamp placements. */
  text: Record<string, TextPlacement[]>;
  /** logicalField (boolean/derived) -> checkbox placements that get an "X" when truthy. */
  checkboxes?: Record<string, CheckboxPlacement[]>;
  /** logicalField -> signature image placements. */
  signatures: Record<string, SignaturePlacement[]>;
}

/** Minimum identity every signing payload exposes to the shared integrity flow. */
export interface SigningIdentity {
  /** The natural person's legal name used for the signing event. */
  signerName: string;
  email: string;
  signerTitle?: string;
  signerCapacity?: string;
}

/** Raw data collected once from the signer via the web form. */
export interface SignerData extends SigningIdentity {
  /** Versioned source-complete profile; never stamped on the agreement. */
  intakeProfile?: NativeIntakeProfile;
  signerName: string;
  practiceName: string;
  legalCompanyName: string;
  /** Office phone (digits). */
  phone: string;
  addressStreet: string;
  city: string;
  state: string;
  zip: string;
  signerTitle: string;
  signerCapacity: string;

  /**
   * Staff-only practice / operations fields. Never stamped onto the PDF.
   * Optional keys are omitted when empty so the portal stays uncluttered.
   */
  mobilePhone?: string;
  pocFirstName?: string;
  pocLastName?: string;
  pocEmail?: string;
  entityType?: string;
  locationCount?: string;
  practiceType?: string;
  npi?: string;
  website?: string;
  ownership?: string;
  yearsInPractice?: string;
  ownerDentistCount?: string;
  dentistCount?: string;
  hygienistCount?: string;
  operatoryCount?: string;
  fteCount?: string;
  activePatientCount?: string;
  monthlyVisits?: string;
  annualRevenueRange?: string;
  annualCardVolumeRange?: string;
  cardProcessingFeeRange?: string;
  intraoralScanner?: string;
  facility?: string;
  consideringSellOrAcquisition?: string;
  anticipatingAcquisition?: string;
  practiceSoftware?: string;
  practiceSoftwareOther?: string;
  daysOpen?: string;
  typicalHours?: string;
  insuranceCarriers?: string;
  insuranceOther?: string;
  procedures?: string;
  proceduresOther?: string;
}

/** Data required to finalize: signer data + signature + consent. */
export interface FinalizePayload extends SignerData {
  signingToken: string;
  /** PNG data URL from signature_pad. */
  signatureImage: string;
  consentAccepted: boolean;
}

export type SigningStatus =
  | "created"
  | "opened"
  | "data_completed"
  | "review_generated"
  | "consented"
  | "signed"
  | "finalized";

export interface SigningAuditRecord {
  submissionId: string;
  signingRequestId: string;
  /** Which registered form was signed (e.g. `agreement`). */
  formKey?: string;
  documentVersion: string;
  sourcePdfSha256: string;
  finalPdfSha256: string;
  signerName: string;
  signerEmail: string;
  signerTitle?: string;
  signerCapacity?: string;
  emailVerifiedAt?: string;
  reviewDisplayedAt: string;
  consentAcceptedAt: string;
  signedAt: string;
  consentTextVersion: string;
  ipAddress?: string;
  userAgent?: string;
}

// Bumped to 1.1 when the W-9 certification clause was removed (bank/tax/W-9
// collection was dropped pending a packet/provider pivot).
export const CONSENT_TEXT_VERSION = "1.1";

export const CONSENT_TEXT =
  "I have reviewed the documents displayed above, agree to conduct this " +
  "transaction electronically, and intend the electronic signature below to " +
  "sign and be legally bound by these documents.";
export type { TeamPerformanceActor, TeamPerformanceMetric, TeamPerformanceSelection, TeamPerformanceCounts, TeamPerformancePerson, TeamPerformanceMember, TeamPerformanceSnapshot, TeamPerformanceRecord, TeamPerformanceRecords, TeamPerformanceRecordQuery } from '../server/teamPerformance/types.js';
